import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Play, ArrowUpRight, BookOpen } from "lucide-react";
import { api } from "../lib/api";
import { parseYoutubeUrl } from "../lib/youtube";
import YoutubePlayer from "../components/YoutubePlayer";
import Seo from "../components/Seo";

const trackDownload = (slug, setCount) => {
  api.post(`/articles/${slug}/download`).then(r => setCount?.(r.data.count)).catch(() => {});
};

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [downloads, setDownloads] = useState(0);

  useEffect(() => {
    setArticle(null); setNotFound(false);
    api.get(`/articles/${slug}`).then(r => setArticle(r.data)).catch(() => setNotFound(true));
    api.get(`/articles/${slug}/download`).then(r => setDownloads(r.data.count)).catch(() => {});
  }, [slug]);

  if (notFound) return (
    <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="article-not-found">
      <div className="overline text-olive mb-3">404</div>
      <h1 className="font-serif text-5xl">We haven't been there yet.</h1>
      <Link to="/destinations" className="btn-outline mt-10 inline-block">Back to destinations</Link>
    </main>
  );

  if (!article) return <main className="min-h-screen bg-bg" data-testid="article-loading" />;

  return (
    <main data-testid="article-page" data-slug={article.slug}>
      <Seo
        title={article.title}
        description={article.excerpt}
        image={article.cover}
        path={`/articles/${article.slug}`}
        type="article"
        article={article}
      />
      {/* Breadcrumb on the clean cream background — always legible */}
      <div className="max-w-8xl mx-auto px-6 md:px-12 pt-6 pb-2">
        <Link to="/destinations" className="overline text-muted hover:text-olive inline-flex items-center gap-2 transition-colors" data-testid="article-back-link">
          <ArrowLeft size={14}/> Back to destinations
        </Link>
      </div>

      {/* Hero — just the artwork, title and subtitle. Action row sits beneath. */}
      <section className="relative aspect-[2/1] w-full overflow-hidden">
        <img src={article.cover} alt={article.title} className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent"/>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-8xl mx-auto px-6 md:px-12 pb-12 md:pb-16 text-bg w-full">
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl [text-shadow:0_2px_10px_rgba(0,0,0,0.55)]">{article.title}</h1>
            <p className="font-serif text-2xl md:text-3xl text-bg mt-6 max-w-3xl italic [text-shadow:0_1px_5px_rgba(0,0,0,0.5)]">{article.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Meta row + action buttons on the clean cream background */}
      <div className="max-w-8xl mx-auto px-6 md:px-12 pt-8 border-b border-line pb-8">
        <div className="flex items-center gap-x-6 gap-y-3 flex-wrap">
          <span className="overline text-olive">{article.destination} · {article.region}</span>
          <span className="overline text-muted">Published {article.published}</span>
          {/* Public download count intentionally hidden — counts are still recorded in the database. */}
          {false && downloads > 0 && (
            <span className="text-[0.7rem] uppercase tracking-[0.18em] text-muted" data-testid="article-download-count">
              {downloads.toLocaleString()} {downloads === 1 ? "download" : "downloads"}
            </span>
          )}
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            {article.pdf_url && (
              <a href={article.pdf_url} target="_blank" rel="noreferrer" download onClick={() => trackDownload(article.slug, setDownloads)} className="inline-flex items-center justify-center gap-2 bg-olive text-bg h-11 w-[220px] text-[0.72rem] uppercase tracking-[0.18em] font-semibold hover:bg-olive-deep transition-colors" data-testid="article-hero-pdf-btn">
                <Download size={14} strokeWidth={1.8}/> Download
              </a>
            )}
            {article.video_url && (
              <a href={article.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-ochre text-ink h-11 w-[220px] text-[0.72rem] uppercase tracking-[0.18em] font-semibold hover:bg-clay hover:text-bg transition-colors" data-testid="article-hero-video-btn">
                <Play size={14} strokeWidth={1.8}/> Watch
              </a>
            )}
          </div>
        </div>
      </div>

      <JumpBar article={article}/>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-6 md:px-0 py-20 md:py-24 prose-editorial">
        <div className="border-l-4 border-olive bg-alt px-6 md:px-10 py-7 md:py-9 mb-14" data-testid="article-intro-block">
          <div className="overline text-olive mb-3">Introduction</div>
          <p className="font-serif text-[1.5rem] md:text-[1.875rem] leading-[1.4] text-ink italic font-semibold !m-0">
            {article.excerpt}
          </p>
        </div>

        {article.sections?.map((s) => {
          const isMeta = ["Concept", "Reflection", "Watch & read"].includes(s.heading);
          const isClosing = s.heading === "Watch & read";
          const isReflection = s.heading === "Reflection";
          const showWebsiteAfter = article.website && (article.website.after_section || article.sections?.[0]?.heading) === s.heading;
          let metaMt = "mt-2";
          if (isReflection) metaMt = "mt-14";
          else if (isClosing) metaMt = "mt-10";
          return (
            <div key={s.heading} data-testid={`article-section-${s.heading.toLowerCase().replace(/\s+/g, "-")}`}>
              {isMeta ? (
                <div className={`overline text-olive ${metaMt} mb-3`}>{s.heading}</div>
              ) : (
                <h3 className="text-olive">{s.heading}</h3>
              )}
              {isClosing ? (
                <p className="font-serif text-2xl md:text-3xl italic text-ink leading-snug">{linkify(s.body)}</p>
              ) : (
                <p>{linkify(s.body)}</p>
              )}
              {article.sections.indexOf(s) === (article.pull_quote_after ?? 1) && article.pull_quote && <blockquote>{article.pull_quote}</blockquote>}
              {showWebsiteAfter && <WebsiteButton website={article.website}/>}
            </div>
          );
        })}
      </section>

      {/* Video / Watch section — constrained, no full-bleed black */}
      <ArticleVideoSection article={article}/>

      {/* Secondary image intentionally removed — articles read cleaner without it. */}

      <section id="article-book" className="scroll-mt-24 bg-alt border-t border-line">
        <div className="max-w-5xl mx-auto px-6 py-20">
          {article.book_cover ? (
            <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center" data-testid="keep-reading-with-cover">
              <div className="md:col-span-5">
                <a href={article.pdf_url || "#"} target={article.pdf_url ? "_blank" : undefined} rel="noreferrer" onClick={article.pdf_url ? () => trackDownload(article.slug, setDownloads) : undefined} className="block group" data-testid="keep-reading-cover-link">
                  <img src={article.book_cover} alt={`${article.title} travel guide cover`} className="w-full h-auto shadow-xl ring-1 ring-line/60 transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-2xl"/>
                </a>
              </div>
              <div className="md:col-span-7 md:text-left text-center">
                <div className="overline text-olive mb-4">Read</div>
                <h3 className="font-serif text-4xl md:text-5xl leading-tight mb-6">Take the full travel guide with you</h3>
                <p className="text-muted mb-8 max-w-md md:max-w-none mx-auto md:mx-0">The downloadable travel guide provides a detailed commentary along with a collection of pictures. The guide is intended to inform and encourage you.</p>
                <div className="flex flex-wrap gap-4 md:justify-start justify-center">
                  {article.pdf_url && (
                    <a href={article.pdf_url} target="_blank" rel="noreferrer" download onClick={() => trackDownload(article.slug, setDownloads)} className="inline-flex items-center justify-center gap-2 bg-olive text-bg hover:bg-olive-deep h-12 w-[280px] text-[0.78rem] uppercase tracking-[0.18em] font-semibold transition-colors" data-testid="article-footer-pdf-btn">
                      <Download size={16} strokeWidth={1.8}/> Download
                    </a>
                  )}
                  <Link to="/destinations" className="inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-ink hover:text-bg h-12 w-[280px] text-[0.78rem] uppercase tracking-[0.18em] font-semibold transition-colors" data-testid="article-more-link">Return to all destinations</Link>
                </div>
              </div>
            </div>
          ) : article.pdf_url ? (
            <div className="max-w-3xl mx-auto text-center">
              <div className="overline text-olive mb-4">Read</div>
              <h3 className="font-serif text-4xl mb-8">Take the full travel guide with you</h3>
              <p className="text-muted mb-8 max-w-md mx-auto">The downloadable travel guide provides a detailed commentary along with a collection of pictures. The guide is intended to inform and encourage you.</p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href={article.pdf_url} target="_blank" rel="noreferrer" download onClick={() => trackDownload(article.slug, setDownloads)} className="inline-flex items-center justify-center gap-2 bg-olive text-bg hover:bg-olive-deep h-12 w-[280px] text-[0.78rem] uppercase tracking-[0.18em] font-semibold transition-colors" data-testid="article-footer-pdf-btn">
                  <Download size={16} strokeWidth={1.8}/> Download
                </a>
                <Link to="/destinations" className="inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-ink hover:text-bg h-12 w-[280px] text-[0.78rem] uppercase tracking-[0.18em] font-semibold transition-colors" data-testid="article-more-link">Return to all destinations</Link>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto text-center">
              <div className="overline text-olive mb-4">Keep exploring</div>
              <h3 className="font-serif text-4xl mb-10">More destinations to discover</h3>
              <Link to="/destinations" className="inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-ink hover:text-bg h-12 w-[280px] text-[0.78rem] uppercase tracking-[0.18em] font-semibold transition-colors" data-testid="article-more-link">Return to all destinations</Link>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}


// --- Helpers --------------------------------------------------------------

// Turn URLs (and markdown-style [label](url)) inside section body text into
// styled <a> links. Lets the editor drop any link into any article without
// needing schema changes.
const _urlRe = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(https?:\/\/[^\s)]+|www\.[^\s)]+)/g;
function linkify(text) {
  if (typeof text !== "string") return text;
  const out = [];
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = _urlRe.exec(text)) !== null) {
    if (m.index > lastIndex) out.push(text.slice(lastIndex, m.index));
    const [, , mdLabel, mdUrl, bareUrl] = m;
    const href = mdUrl || (bareUrl?.startsWith("www.") ? `https://${bareUrl}` : bareUrl);
    const label = mdLabel || bareUrl?.replace(/^https?:\/\//, "").replace(/\/$/, "");
    out.push(
      <a key={`lnk-${key++}`} href={href} target="_blank" rel="noreferrer" className="text-olive underline underline-offset-4 decoration-olive/40 hover:decoration-olive transition-colors">
        {label}
      </a>
    );
    lastIndex = _urlRe.lastIndex;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out.length ? out : text;
}

function JumpBar({ article }) {
  const hasVideos = parseYoutubeUrl(article.video_url) || article.videos?.length;
  if ((article.sections?.length || 0) < 6 || (!hasVideos && !article.book_cover)) return null;
  const go = (id) => (e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };
  const linkCls = "inline-flex items-center gap-2 text-ink hover:text-olive border-b border-transparent hover:border-olive transition-colors";
  return (
    <nav className="max-w-3xl mx-auto px-6 md:px-0 pt-10 -mb-10 flex items-center gap-6 flex-wrap" data-testid="article-jump-bar">
      <span className="overline text-muted">Jump to</span>
      {hasVideos && <a href="#article-videos" onClick={go("article-videos")} className={`overline ${linkCls}`} data-testid="jump-to-videos"><Play size={13} strokeWidth={1.8}/> Videos</a>}
      {article.book_cover && <a href="#article-book" onClick={go("article-book")} className={`overline ${linkCls}`} data-testid="jump-to-book"><BookOpen size={13} strokeWidth={1.8}/> The Book</a>}
    </nav>
  );
}

function pickVideoUrl(v) { return typeof v === "string" ? v : v?.url; }
function pickVideoLabel(v) { return typeof v === "string" ? null : v?.label; }

function videoSubheading(article) {
  const hasMulti = article.videos && article.videos.length > 1;
  if (hasMulti) {
    const n = article.videos.length;
    const words = { 2: "Two", 3: "Three", 4: "Four", 5: "Five", 6: "Six", 7: "Seven", 8: "Eight", 9: "Nine", 10: "Ten", 11: "Eleven", 12: "Twelve" };
    const count = words[n] || String(n);
    return `${count} short videos from the journey — click any thumbnail to open it on YouTube.`;
  }
  if (parseYoutubeUrl(article.video_url)) return "Press play to open the playlist on YouTube.";
  return "A film for this piece is in production. In the meantime, browse the full channel.";
}

function ArticleVideoSection({ article }) {
  const hasMulti = article.videos && article.videos.length > 1;
  const hasAny = parseYoutubeUrl(article.video_url) || (article.videos && article.videos.length);
  const singleUrl = article.video_url || pickVideoUrl(article.videos?.[0]);

  // Articles with no video at all (and no video_heading set) skip the Watch section entirely.
  if (!hasAny && !article.video_heading) return null;

  return (
    <section id="article-videos" className="scroll-mt-24 max-w-8xl mx-auto px-6 md:px-12 pb-12 border-t border-line pt-12 md:pt-16" data-testid="article-video-section">
      <div className="flex items-end justify-between mb-8 gap-6">
        <div>
          <div className="overline text-olive mb-3">Watch</div>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">{article.video_heading || `The ${article.destination} film`}</h2>
          <p className="mt-2 text-muted text-sm max-w-xl">{videoSubheading(article)}</p>
        </div>
      </div>

      {hasMulti && (() => {
        const n = article.videos.length;
        let cols = "lg:grid-cols-3";
        if (n === 2 || n === 4) cols = "lg:grid-cols-2";
        else if (n === 9) cols = "lg:grid-cols-3";
        else if (n >= 8) cols = "lg:grid-cols-4";
        return (
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols} gap-x-6 gap-y-10`} data-testid="article-video-grid">
          {article.videos.map((v, i) => {
            const url = pickVideoUrl(v);
            const label = pickVideoLabel(v);
            const num = String(i + 1).padStart(2, "0");
            return (
              <div key={url || `video-${i}`} className="flex flex-col" data-testid={`article-video-${i + 1}-card`}>
                <YoutubePlayer url={url} title={`${article.title} — ${label || `video ${i + 1}`}`} testId={`article-video-${i + 1}`} posterOverride={v && v.poster ? v.poster : null}/>
                {label && (
                  <div className="mt-3 flex items-baseline gap-3" data-testid={`article-video-${i + 1}-caption`}>
                    <span className="font-serif text-ochre text-xl tracking-tight">{num}</span>
                    <span className="overline text-ink">{label}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        );
      })()}

      {!hasMulti && hasAny && (
        <YoutubePlayer url={singleUrl} title={article.title} testId="article-video"/>
      )}

      {!hasAny && (
        <a href="https://www.youtube.com/@thewheretogo" target="_blank" rel="noreferrer" className="group relative block w-full bg-olive overflow-hidden" style={{ paddingTop: "42%" }} data-testid="article-video-fallback">
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-bg">
            <div className="w-20 h-20 bg-ochre flex items-center justify-center group-hover:bg-clay transition-colors">
              <Play size={32} strokeWidth={1.8} fill="currentColor"/>
            </div>
            <div className="overline text-bg/80">Visit The Where To Go on YouTube</div>
          </div>
        </a>
      )}
    </section>
  );
}

function WebsiteButton({ website }) {
  // `website` may be a plain URL string or an object: { url, label, color, textColor, after_section }
  const cfg = typeof website === "string" ? { url: website } : (website || {});
  const url = cfg.url;
  if (!url) return null;
  const label = cfg.label || "Visit the website";
  const bg = cfg.color || "#5b2980"; // default deep olive-friendly fallback
  const fg = cfg.textColor || "#ffffff";
  return (
    <div className="not-prose my-12 flex justify-center" data-testid="article-website-btn-wrap">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        style={{ backgroundColor: bg, color: fg }}
        className="inline-flex items-center gap-2 px-7 h-12 text-[0.78rem] uppercase tracking-[0.18em] font-semibold transition-transform hover:-translate-y-0.5 hover:shadow-lg"
        data-testid="article-website-btn"
      >
        {label}
        <ArrowUpRight size={16} strokeWidth={2}/>
      </a>
    </div>
  );
}
